import {
  Box,
  IconButton,
  SpriteIcon,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@coolblue-development/freddy';
import readPropertyExplanationTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/readPropertyExplanationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import {
  ShortSpecificationTableCell,
  SpecificationInfoCell,
  SpecificationTableCell
} from '@features/product/components/Specifications/PropertiesTable.styled';
import type { Property } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, useState } from 'react';

import SpecificationLink from './SpecificationLink';

type Props = {
  properties: Property[];
  productTypeId: number;
  title?: string;
  variant?: 'base' | 'striped' | 'bordered';
  isShort?: boolean;
};

function PropertiesTable({ properties, productTypeId, variant, title, isShort }: Props) {
  const { t } = useTranslation('webshop');
  const { isMobile } = useBreakpoints();
  const { trackEvent } = useAnalytics();
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const SpecificationCell = isShort ? ShortSpecificationTableCell : SpecificationTableCell;

  const renderValue = (value: string): ReactNode => {
    if (value === 'true') {
      return (
        <SpriteIcon name='checkmark' aria-label={t('productPage.yes')} color='sentimentPositive' />
      );
    }

    if (value === 'false') {
      return <SpriteIcon name='minus' aria-label={t('productPage.no')} color='sentimentNegative' />;
    }

    return value;
  };

  const onExplanationClick = (property: Property) => () => {
    if (!property.hasExplanation) {
      return;
    }

    setSelectedProperty(property);
    trackEvent(
      readPropertyExplanationTrigger({
        sub_feature: isShort ? 'short_specifications' : 'specifications',
        property_name: property.displayName || '',
        property_value: property.value || ''
      })
    );
  };

  const hasAnyExplanation = properties.find(property => property.hasExplanation);

  return (
    <Box>
      {title && <Typography variant='h3'>{title}</Typography>}
      <Table
        variant={variant || (isMobile || properties.length === 1 ? 'base' : 'striped')}
        width='100%'
      >
        <TableBody>
          {properties?.map(property => (
            <TableRow key={property.propertyId} onClick={onExplanationClick(property)}>
              {hasAnyExplanation && (
                <SpecificationInfoCell width={32}>
                  {property.hasExplanation && (
                    <IconButton
                      aria-label={t('productPage.specificationsInfo', {
                        spec: property.displayName
                      })}
                    >
                      <SpriteIcon name='info-circled' color='base' />
                    </IconButton>
                  )}
                </SpecificationInfoCell>
              )}
              <SpecificationCell>
                {isMobile && property.hasExplanation ? (
                  <SpecificationLink component='button'>{property.displayName}</SpecificationLink>
                ) : (
                  <Typography color={isMobile ? 'additional' : 'base'}>
                    {property.displayName}
                  </Typography>
                )}
              </SpecificationCell>
              <TableCell>{renderValue(property.value || '')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {selectedProperty && (
        <PropertyExplanationModal
          open={!!selectedProperty}
          onClose={() => setSelectedProperty(null)}
          property={{
            ...selectedProperty,
            displayName: selectedProperty.displayName || '',
            hasExplanation: Boolean(selectedProperty.hasExplanation),
            propertyId: String(selectedProperty.propertyId)
          }}
          productTypeId={productTypeId.toString()}
        />
      )}
    </Box>
  );
}

export default PropertiesTable;
